import styled from 'styled-components';

// Button components
export const Button = styled.button`
  padding: 8px 16px;
  background-color: var(--primary-color);
  color: white;
  border-radius: var(--border-radius);
  font-weight: 600;
  transition: var(--transition);
  font-size: 1rem;
  min-height: 40px;
  
  &:hover {
    background-color: #e07a10;
  }
  
  &:disabled {
    background-color: var(--medium-gray);
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 10px 18px;
    font-size: 1rem;
    min-height: 44px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    min-height: 48px;
  }
`;

export const SecondaryButton = styled(Button)`
  background-color: var(--secondary-color);
  
  &:hover {
    background-color: #0f0f30;
  }
`;

export const OutlineButton = styled(Button)`
  background-color: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
  }
`;

// Card components
export const Card = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    border-radius: 6px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--light-gray);
  
  h2, h3, h4 {
    margin: 0;
    color: var(--secondary-color);
  }
`;

export const CardBody = styled.div`
  margin-bottom: 1rem;
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--light-gray);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

// Form components
export const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--dark-gray);
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--medium-gray);
  border-radius: var(--border-radius);
  font-size: 1rem;
  
  &:focus {
    border-color: var(--primary-color);
    outline: none;
    box-shadow: 0 0 0 2px rgba(246, 139, 33, 0.2);
  }
  
  &.error {
    border-color: var(--danger-color);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 0.9rem;
    font-size: 1.1rem;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--medium-gray);
  border-radius: var(--border-radius);
  background-color: white;
  font-size: 1rem;
  
  &:focus {
    border-color: var(--primary-color);
    outline: none;
    box-shadow: 0 0 0 2px rgba(246, 139, 33, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 0.9rem;
    font-size: 1.1rem;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--medium-gray);
  border-radius: var(--border-radius);
  min-height: 100px;
  resize: vertical;
  font-size: 1rem;
  font-family: var(--font-family);
  
  &:focus {
    border-color: var(--primary-color);
    outline: none;
    box-shadow: 0 0 0 2px rgba(246, 139, 33, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 1rem;
    min-height: 120px;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 0.9rem;
    font-size: 1.1rem;
    min-height: 140px;
  }
`;

// Alias for backward compatibility
export const Textarea = TextArea;

export const ErrorText = styled.div`
  color: var(--danger-color);
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

// Layout components
export const FlexContainer = styled.div<{ gap?: string; align?: string; justify?: string; direction?: string }>`
  display: flex;
  gap: ${props => props.gap || '1rem'};
  align-items: ${props => props.align || 'center'};
  justify-content: ${props => props.justify || 'flex-start'};
  flex-direction: ${props => props.direction || 'row'};
  
  @media (max-width: 768px) {
    flex-direction: ${props => 
      props.direction === 'row' ? 'column' : 
      props.direction === 'column' ? 'column' : 
      'column'
    };
    gap: ${props => props.gap === '2rem' ? '1rem' : props.gap || '0.75rem'};
    align-items: ${props => props.align || 'stretch'};
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

export const Grid = styled.div<{ columns?: string; gap?: string }>`
  display: grid;
  grid-template-columns: ${props => props.columns || 'repeat(auto-fill, minmax(250px, 1fr))'};
  gap: ${props => props.gap || '1rem'};
  
  @media (max-width: 1200px) {
    grid-template-columns: ${props => 
      props.columns === 'repeat(4, 1fr)' ? 'repeat(2, 1fr)' : 
      props.columns === 'repeat(3, 1fr)' ? 'repeat(2, 1fr)' : 
      props.columns || 'repeat(auto-fill, minmax(200px, 1fr))'
    };
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${props => props.gap === '2rem' ? '1rem' : props.gap || '0.75rem'};
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

// Badge component
export const Badge = styled.span<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.variant) {
      case 'primary': return 'var(--primary-color)';
      case 'secondary': return 'var(--secondary-color)';
      case 'success': return 'var(--success-color)';
      case 'danger': return 'var(--danger-color)';
      case 'warning': return 'var(--warning-color)';
      case 'info': return 'var(--info-color)';
      default: return 'var(--light-gray)';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'primary':
      case 'secondary':
      case 'success':
      case 'danger':
        return 'white';
      case 'warning':
      case 'info':
      default:
        return 'var(--text-color)';
    }
  }};
`;

// Table components
export const TableContainer = styled.div`
  overflow-x: auto;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  
  @media (max-width: 768px) {
    min-width: 500px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    min-width: 400px;
    font-size: 0.8rem;
  }
`;

export const TableHead = styled.thead`
  background-color: var(--light-gray);
  
  @media (max-width: 768px) {
    background-color: var(--secondary-color);
    color: white;
  }
`;

export const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  &:hover {
    background-color: rgba(246, 139, 33, 0.05);
  }
  
  @media (max-width: 768px) {
    &:hover {
      background-color: rgba(246, 139, 33, 0.1);
    }
  }
`;

export const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid var(--light-gray);
  vertical-align: top;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.4rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.3rem;
  }
`;

export const TableHeader = styled.th`
  padding: 0.75rem;
  text-align: left;
  border-bottom: 2px solid var(--medium-gray);
  color: var(--secondary-color);
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.4rem;
    color: white;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.3rem;
    font-size: 0.8rem;
  }
`;

// Pagination
export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  gap: 0.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.3rem;
    margin-top: 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.2rem;
    justify-content: space-between;
  }
`;

export const PageButton = styled.button<{ active?: boolean }>`
  padding: 0.25rem 0.75rem;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : 'var(--medium-gray)'};
  background-color: ${props => props.active ? 'var(--primary-color)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--text-color)'};
  border-radius: var(--border-radius);
  min-width: 40px;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--light-gray)'};
  }
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.6rem;
    font-size: 0.9rem;
    min-width: 36px;
  }
  
  @media (max-width: 480px) {
    padding: 0.15rem 0.5rem;
    font-size: 0.8rem;
    min-width: 32px;
  }
`;

// Mobile-friendly table components
export const MobileTable = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

export const MobileTableItem = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid var(--primary-color);
`;

export const MobileTableHeader = styled.div`
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

export const MobileTableRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
  border-bottom: 1px solid var(--light-gray);
  
  &:last-child {
    border-bottom: none;
  }
`;

export const MobileTableLabel = styled.span`
  font-weight: 500;
  color: var(--dark-gray);
  font-size: 0.9rem;
`;

export const MobileTableValue = styled.span`
  color: var(--text-color);
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
`;

export const DesktopTable = styled.div`
  display: block;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

// Developer Tag
export const DeveloperTag = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: var(--secondary-color);
  color: white;
  padding: 8px 12px;
  border-radius: var(--border-radius);
  font-size: 0.8rem;
  z-index: 1000;
  
  @media (max-width: 768px) {
    position: static;
    text-align: center;
    margin-top: 2rem;
  }
`;

// Progress Bar Component
export const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 8px;
  background-color: var(--light-gray);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
  
  &::after {
    content: '';
    display: block;
    width: ${props => Math.min(props.progress, 100)}%;
    height: 100%;
    background-color: ${props => 
      props.progress >= 100 ? 'var(--danger-color)' : 
      props.progress >= 75 ? 'var(--warning-color)' : 
      'var(--primary-color)'
    };
    transition: width 0.3s ease;
  }
`;

// Loading Spinner Component
export const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--primary-color);
  font-size: 1.1rem;
  font-weight: 500;
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid var(--light-gray);
    border-top: 2px solid var(--primary-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 0.75rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    font-size: 1rem;
    
    &::before {
      width: 18px;
      height: 18px;
      margin-right: 0.5rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.9rem;
    
    &::before {
      width: 16px;
      height: 16px;
      margin-right: 0.4rem;
    }
  }
`;

// Empty State Component
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: var(--medium-gray);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.6;
  }
  
  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--dark-gray);
  }
  
  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    max-width: 400px;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    
    svg {
      width: 48px;
      height: 48px;
      margin-bottom: 0.75rem;
    }
    
    h3 {
      font-size: 1.1rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
    
    svg {
      width: 40px;
      height: 40px;
      margin-bottom: 0.5rem;
    }
    
    h3 {
      font-size: 1rem;
    }
    
    p {
      font-size: 0.85rem;
    }
  }
`;

// Modal Components
export const Modal = styled.div<{ isOpen?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
`;

export const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  z-index: 1001;
  
  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
    margin: 1rem;
  }
`;

export const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--light-gray);
  
  h2, h3 {
    margin: 0;
    color: var(--secondary-color);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1rem 0.75rem;
  }
`;

export const ModalBody = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--light-gray);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem 1rem;
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

// Form Components
export const FormControl = styled.div`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

export const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--dark-gray);
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

// Table Components
export const TableBody = styled.tbody`
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`; 